import React, { useCallback, useEffect, useMemo, useState } from "react";
import { motion } from "motion/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleCheck,
  faCircleExclamation,
  faChevronDown,
  faChevronRight,
} from "@awesome.me/kit-70c1f0055f/icons/classic/regular";

import { Button } from "@/components/ui/button/Button";
import type { OsAniContextSnapshot } from "@/components/ui/os-layout/buildOsAniContextSnapshot";
import { feToolRegistry, type FeToolResult, type FeToolSchema } from "@/services/agent";
import {
  fetchFeToolPickerArtifacts,
  fetchFeToolPickerReviewTasks,
} from "@/services/api/chatDebugMenu";
import {
  buildInitialFeToolParams,
  compactFeToolParams,
  formatFeToolParamsJson,
  parseFeToolParamsJson,
} from "./feToolDebug/buildParams";
import { FeToolDebugForm } from "./feToolDebug/FeToolDebugForm";
import type { FeToolDebugPickerOptions } from "./feToolDebug/toolForms";

export type FeToolDebugRunner = (
  toolId: string,
  params: Record<string, unknown>,
) => Promise<FeToolResult>;

interface AniFeToolDebugPanelProps {
  enabled: boolean;
  runTool: FeToolDebugRunner;
  osContext?: OsAniContextSnapshot;
  feOpenAppAllowlist?: readonly string[];
  /** When true, omit outer card (parent DeveloperMenuSection provides the container). */
  embedded?: boolean;
}

function sortTools(tools: FeToolSchema[]): FeToolSchema[] {
  return [...tools].sort((a, b) => a.id.localeCompare(b.id));
}

export function AniFeToolDebugPanel({
  enabled,
  runTool,
  osContext,
  feOpenAppAllowlist,
  embedded = false,
}: AniFeToolDebugPanelProps) {
  const tools = useMemo(
    () => sortTools(feToolRegistry.getToolSchemasForApp("general")),
    [],
  );
  const [toolId, setToolId] = useState(() => tools[0]?.id ?? "fe_show");
  const [params, setParams] = useState<Record<string, unknown>>(() =>
    buildInitialFeToolParams(toolId, feToolRegistry.getTool(toolId)),
  );
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [paramsText, setParamsText] = useState(() => formatFeToolParamsJson(params));
  const [running, setRunning] = useState(false);
  const [lastResult, setLastResult] = useState<FeToolResult | null>(null);
  const [pickerOptions, setPickerOptions] = useState<FeToolDebugPickerOptions>({
    artifacts: [],
    reviewTasks: [],
  });

  useEffect(() => {
    if (!enabled) {
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const [artifacts, tasks] = await Promise.all([
          fetchFeToolPickerArtifacts(),
          fetchFeToolPickerReviewTasks(),
        ]);
        if (!cancelled) {
          setPickerOptions({
            artifacts: artifacts.items,
            reviewTasks: tasks.items,
          });
        }
      } catch {
        if (!cancelled) {
          setPickerOptions({ artifacts: [], reviewTasks: [] });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  const onToolChange = useCallback((nextId: string) => {
    setToolId(nextId);
    const schema = feToolRegistry.getTool(nextId);
    const nextParams = buildInitialFeToolParams(nextId, schema);
    setParams(nextParams);
    setParamsText(formatFeToolParamsJson(nextParams));
    setLastResult(null);
    setAdvancedOpen(false);
  }, []);

  const selectedTool = tools.find((tool) => tool.id === toolId);

  const syncParamsFromAdvanced = useCallback(() => {
    const parsed = parseFeToolParamsJson(paramsText);
    if (parsed.ok) {
      setParams(parsed.value);
    }
  }, [paramsText]);

  const handleExecute = useCallback(async () => {
    if (!enabled || !toolId) {
      return;
    }

    let parsed: Record<string, unknown>;
    if (advancedOpen) {
      const result = parseFeToolParamsJson(paramsText);
      if (!result.ok) {
        setLastResult({ success: false, message: result.message });
        return;
      }
      parsed =
        Object.keys(result.value).length > 0
          ? result.value
          : buildInitialFeToolParams(toolId, selectedTool);
    } else {
      parsed = compactFeToolParams(params);
      if (Object.keys(parsed).length === 0) {
        parsed = buildInitialFeToolParams(toolId, selectedTool);
      }
    }

    setRunning(true);
    setLastResult(null);
    try {
      const result = await runTool(toolId, parsed);
      setLastResult(result);
    } catch (error) {
      setLastResult({
        success: false,
        message: error instanceof Error ? error.message : "Tool execution failed",
      });
    } finally {
      setRunning(false);
    }
  }, [advancedOpen, enabled, params, paramsText, runTool, selectedTool, toolId]);

  const content = (
    <>
        {!enabled ? (
          <p className="text-xs text-slate-500">Enable Developer menu as admin to run FE tools manually.</p>
        ) : tools.length === 0 ? (
          <p className="text-xs text-slate-500">No FE tools registered yet.</p>
        ) : (
          <div className="space-y-3">
            <label className="block space-y-1">
              <span className="text-[11px] font-medium uppercase tracking-wide text-slate-500">Tool</span>
              <select
                className="w-full rounded-lg border border-slate-600/60 bg-slate-900/40 px-2 py-1.5 text-sm text-slate-100"
                value={toolId}
                onChange={(event) => onToolChange(event.target.value)}
                data-testid="ani-fe-tool-debug-select"
              >
                {tools.map((tool) => (
                  <option key={tool.id} value={tool.id}>
                    {tool.id}
                  </option>
                ))}
              </select>
            </label>

            {selectedTool?.description ? (
              <p className="text-[11px] text-slate-400">{selectedTool.description}</p>
            ) : null}

            {selectedTool ? (
              <FeToolDebugForm
                schema={selectedTool}
                params={params}
                onChange={(next) => {
                  setParams(next);
                  setParamsText(formatFeToolParamsJson(next));
                }}
                osContext={osContext}
                feOpenAppAllowlist={feOpenAppAllowlist}
                pickerOptions={pickerOptions}
              />
            ) : null}

            <div className="rounded-lg border border-slate-700/40">
              <button
                type="button"
                className="flex w-full items-center justify-between gap-2 px-2 py-2 text-left text-[11px] uppercase tracking-wide text-slate-500"
                aria-expanded={advancedOpen}
                onClick={() => {
                  if (!advancedOpen) {
                    setParamsText(formatFeToolParamsJson(params));
                  } else {
                    syncParamsFromAdvanced();
                  }
                  setAdvancedOpen((prev) => !prev);
                }}
                data-testid="fe-tool-debug-advanced-toggle"
              >
                <span>Advanced JSON</span>
                <FontAwesomeIcon icon={advancedOpen ? faChevronDown : faChevronRight} className="h-3 w-3" />
              </button>
              {advancedOpen ? (
                <div className="border-t border-slate-700/40 px-2 pb-2 pt-2">
                  <textarea
                    className="min-h-[120px] w-full rounded-lg border border-slate-600/60 bg-slate-900/40 px-2 py-1.5 font-mono text-xs text-slate-100"
                    value={paramsText}
                    onChange={(event) => setParamsText(event.target.value)}
                    onBlur={syncParamsFromAdvanced}
                    spellCheck={false}
                    data-testid="ani-fe-tool-debug-params"
                  />
                </div>
              ) : null}
            </div>

            <Button
              type="button"
              color="slate"
              className="w-full !text-sm"
              onClick={() => void handleExecute()}
              loading={running}
              disabled={!toolId}
              data-testid="ani-fe-tool-debug-run"
            >
              Run tool
            </Button>

            {lastResult ? (
              <div
                className="flex items-start gap-2 rounded-lg border border-slate-700/50 p-2 text-xs"
                data-testid="ani-fe-tool-debug-result"
              >
                <FontAwesomeIcon
                  icon={lastResult.success ? faCircleCheck : faCircleExclamation}
                  className={lastResult.success ? "mt-0.5 text-emerald-500" : "mt-0.5 text-amber-500"}
                  aria-hidden="true"
                />
                <div className="min-w-0 flex-1 space-y-1 text-slate-300">
                  <p>{lastResult.message ?? (lastResult.success ? "OK" : "Failed")}</p>
                  {lastResult.data && Object.keys(lastResult.data).length > 0 ? (
                    <pre className="overflow-x-auto whitespace-pre-wrap break-all text-[10px] text-slate-500">
                      {JSON.stringify(lastResult.data, null, 2)}
                    </pre>
                  ) : null}
                </div>
              </div>
            ) : null}
          </div>
        )}
    </>
  );

  if (embedded) {
    return (
      <div className="overflow-hidden" data-testid="ani-fe-tool-debug-panel">
        {content}
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex flex-col gap-2 overflow-hidden"
      data-testid="ani-fe-tool-debug-panel"
    >
      <div className="rounded-xl bg-slate-800/30 px-4 py-3 dark:border-slate-600">{content}</div>
    </motion.div>
  );
}
